import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell,
} from "recharts";
import { DollarSign, ShieldCheck, Ban, Activity, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { ThreatFeed } from "@/components/dashboard/ThreatFeed";
import { FraudHeatmap } from "@/components/dashboard/FraudHeatmap";

const KpiCard = ({ icon: Icon, label, value, accent, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    className="rounded-xl bg-[#0B101A] border border-white/8 p-5"
  >
    <div className="flex items-center justify-between">
      <span className="font-mono text-[10px] tracking-widest uppercase text-slate-500">{label}</span>
      <Icon className={`w-4 h-4 ${accent}`} />
    </div>
    <div className="font-display text-2xl lg:text-3xl text-white mt-3 tabular-nums">{value}</div>
  </motion.div>
);

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg bg-[#040914] border border-white/15 px-3 py-2 shadow-xl">
      <p className="font-mono text-[10px] text-slate-400 mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.name} className="text-xs" style={{ color: p.color }}>
          {p.name}: <span className="font-mono">{p.value.toLocaleString()}</span>
        </p>
      ))}
    </div>
  );
};

const DEVICE_COLORS = ["#00F0FF", "#39FF14", "#7C3AED", "#F59E0B"];

export const Overview = ({ onSetup }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/overview").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data)
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="w-6 h-6 text-cyan-neon animate-spin" />
      </div>
    );

  return (
    <div className="space-y-5">
      <div
        data-testid="overview-data-source"
        className={`flex flex-wrap items-center gap-2 rounded-lg border px-4 py-2.5 ${
          data.data_source === "live"
            ? "bg-[#39FF14]/5 border-[#39FF14]/20"
            : "bg-amber-500/5 border-amber-500/20"
        }`}
      >
        <span className={`w-2 h-2 rounded-full ${data.data_source === "live" ? "bg-[#39FF14] animate-pulse-glow" : "bg-amber-400"}`} />
        {data.data_source === "live" ? (
          <span className="font-mono text-[11px] tracking-wider uppercase text-[#39FF14]">Live data — your tracked traffic</span>
        ) : (
          <>
            <span className="font-mono text-[11px] tracking-wider uppercase text-amber-400">Demo data</span>
            <span className="text-xs text-slate-400">— install your tracking snippet to see your real traffic.</span>
            {onSetup && (
              <button onClick={onSetup} className="text-xs text-cyan-neon hover:underline" data-testid="overview-setup-link">
                Open Tracking Setup →
              </button>
            )}
          </>
        )}
      </div>
      <ThreatFeed />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={ShieldCheck} label="Valid clicks (30d)" value={data.total_valid.toLocaleString()} accent="text-cyan-neon" delay={0} />
        <KpiCard icon={Ban} label="Fraud blocked (30d)" value={data.total_blocked.toLocaleString()} accent="text-[#39FF14]" delay={0.05} />
        <KpiCard icon={DollarSign} label="Money saved" value={`$${data.money_saved.toLocaleString()}`} accent="text-[#39FF14]" delay={0.1} />
        <KpiCard icon={Activity} label="Block rate" value={`${data.block_rate}%`} accent="text-cyan-neon" delay={0.15} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-xl bg-[#0B101A] border border-white/8 p-6">
          <h3 className="font-display text-lg text-white mb-1">Valid vs. Blocked clicks</h3>
          <p className="font-mono text-[10px] tracking-wider uppercase text-slate-500 mb-5">Last 30 days</p>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={data.timeseries} margin={{ left: -18, right: 6 }}>
              <defs>
                <linearGradient id="gValid" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00F0FF" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#00F0FF" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gBlocked" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#39FF14" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#39FF14" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 10 }} tickLine={false} axisLine={false} interval={5} />
              <YAxis tick={{ fill: "#64748B", fontSize: 10 }} tickLine={false} axisLine={false} />
              <Tooltip content={<ChartTooltip />} />
              <Area type="monotone" dataKey="valid" name="Valid" stroke="#00F0FF" strokeWidth={2} fill="url(#gValid)" />
              <Area type="monotone" dataKey="blocked" name="Blocked" stroke="#39FF14" strokeWidth={2} fill="url(#gBlocked)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl bg-[#0B101A] border border-white/8 p-6">
          <h3 className="font-display text-lg text-white mb-1">Traffic by device</h3>
          <p className="font-mono text-[10px] tracking-wider uppercase text-slate-500 mb-5">Sessions</p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.by_device} margin={{ left: -18, right: 6 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="device" tick={{ fill: "#64748B", fontSize: 9 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fill: "#64748B", fontSize: 10 }} tickLine={false} axisLine={false} />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Bar dataKey="value" name="Sessions" radius={[4, 4, 0, 0]}>
                {data.by_device.map((_, i) => (
                  <Cell key={i} fill={DEVICE_COLORS[i % DEVICE_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <FraudHeatmap />

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-xl bg-[#0B101A] border border-white/8 p-6">
          <h3 className="font-display text-lg text-white mb-5">Top blocked IP addresses</h3>
          <div className="space-y-3">
            {data.top_ips.map((ip) => (
              <div key={ip.ip} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                <div>
                  <div className="font-mono text-sm text-white">{ip.ip}</div>
                  <div className="font-mono text-[10px] text-slate-500 mt-0.5">{ip.country} · {ip.isp}</div>
                </div>
                <span className="font-mono text-sm text-[#39FF14]">{ip.attempts.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl bg-[#0B101A] border border-white/8 p-6">
          <h3 className="font-display text-lg text-white mb-5">Traffic by country</h3>
          <div className="space-y-3.5">
            {data.by_country.map((c) => (
              <div key={c.code}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm text-slate-300">{c.country}</span>
                  <span className="font-mono text-xs text-slate-500">{c.clicks.toLocaleString()} · <span className="text-[#39FF14]">{c.blocked_pct}% blocked</span></span>
                </div>
                <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-cyan-neon to-[#39FF14]" style={{ width: `${c.blocked_pct}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
